import "@babylonjs/core/Collisions/collisionCoordinator";
import { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import type { Engine } from "@babylonjs/core/Engines/engine";
import { DirectionalLight } from "@babylonjs/core/Lights/directionalLight";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { PointLight } from "@babylonjs/core/Lights/pointLight";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Scene } from "@babylonjs/core/scene";
import { PLAYER_CONFIG } from "../game/GameConfig";
import type { LevelData } from "../generation/types";
import { gridToWorld } from "./worldCoordinates";

export interface SceneContext {
  readonly scene: Scene;
  readonly camera: UniversalCamera;
  readonly emergencyLight: PointLight;
}

export function createScene(engine: Engine, level: LevelData): SceneContext {
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0.009, 0.012, 0.009, 1);
  scene.collisionsEnabled = true;
  scene.fogMode = Scene.FOGMODE_EXP2;
  scene.fogDensity = 0.01;
  scene.fogColor = new Color3(0.022, 0.029, 0.022);
  scene.ambientColor = new Color3(0.08, 0.09, 0.075);

  const startPosition = gridToWorld(level.playerStart, level.grid);
  const camera = new UniversalCamera(
    "player-camera",
    new Vector3(startPosition.x, PLAYER_CONFIG.cameraHeight, startPosition.z),
    scene,
  );
  camera.minZ = 0.05;
  camera.maxZ = 140;
  camera.fov = 1.12;
  camera.inertia = 0;
  camera.checkCollisions = true;
  camera.ellipsoid = new Vector3(
    PLAYER_CONFIG.collisionRadius,
    PLAYER_CONFIG.cameraHeight * 0.5,
    PLAYER_CONFIG.collisionRadius,
  );
  camera.ellipsoidOffset = new Vector3(0, -PLAYER_CONFIG.cameraHeight * 0.5, 0);
  scene.activeCamera = camera;

  const ambient = new HemisphericLight("ambient", new Vector3(0, 1, 0), scene);
  ambient.intensity = 0.46;
  ambient.diffuse = new Color3(0.46, 0.52, 0.43);
  ambient.groundColor = new Color3(0.075, 0.062, 0.05);

  const directional = new DirectionalLight("directional", new Vector3(-0.4, -1, 0.2), scene);
  directional.intensity = 0.2;
  directional.diffuse = new Color3(0.65, 0.7, 0.58);

  const toxicRoom = level.rooms.find((room) => room.id === "toxic");
  const toxicPosition = gridToWorld(toxicRoom?.center ?? level.exit, level.grid);
  const emergencyLight = new PointLight(
    "emergency",
    new Vector3(toxicPosition.x, 2.7, toxicPosition.z),
    scene,
  );
  emergencyLight.diffuse = new Color3(1, 0.18, 0.08);
  emergencyLight.range = 20;
  emergencyLight.intensity = 0;
  emergencyLight.setEnabled(false);

  return { scene, camera, emergencyLight };
}
